import { keepPreviousData } from '@tanstack/react-query';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithQuery } from 'jotai-tanstack-query';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import {
  InvestorCashFlowRow,
  InvestorCashFlowsResponse,
  InvestorCashFlowType,
} from '../../../../schemas/InvestorCashFlows.schema';
import { getFundInvestorCashFlows } from '../../../../services/queries/MaggieMetricsQueries';
import { selectedDateStringFPState } from '../../state/FPState';

export const InvestorCashFlowsQueryKey = 'fundInvestorCashFlows';

export interface IInvestorCashFlowsDateRange {
  from: string | null; // 'YYYY-MM-DD'
  to: string | null; // 'YYYY-MM-DD'
}

export const EmptyInvestorCashFlowsDateRange: IInvestorCashFlowsDateRange = { from: null, to: null };

// Plain, not per fund: the profile's own as-of atom is plain and persists across funds too.
export const investorCashFlowsTypeJ = atomWithReset<InvestorCashFlowType>('Total');
export const investorCashFlowsDateRangeJ = atomWithReset<IInvestorCashFlowsDateRange>(
  EmptyInvestorCashFlowsDateRange
);
export const investorCashFlowsIncludeFeesJ = atomWithReset(false);

export interface IInvestorCashFlowsBounds {
  from: string | undefined;
  to: string;
}

// The header "As of" is the ceiling for the section, not just its default upper bound: an applied
// range that outlives a move of the as-of is trimmed here, so every fetch stops where the tab does.
export const investorCashFlowsBoundsJ = atom<IInvestorCashFlowsBounds>((get) => {
  const range = get(investorCashFlowsDateRangeJ);
  const asOf = get(selectedDateStringFPState);

  return { from: range.from ?? undefined, to: range.to && range.to < asOf ? range.to : asOf };
});

/**
 * One `Total` fetch per (fund, bounds, fees); the investor type is filtered client-side. The filters
 * sit in the query key rather than the family key so `keepPreviousData` holds the previous rows on
 * the same observer while the next ones load.
 */
export const investorCashFlowsQueryJ = atomFamily((fundId: number) =>
  atomWithQuery<InvestorCashFlowsResponse>((get) => {
    const { from, to } = get(investorCashFlowsBoundsJ);
    const includeFeesAndExpenses = get(investorCashFlowsIncludeFeesJ);

    return {
      queryKey: [InvestorCashFlowsQueryKey, fundId, from ?? '', to, includeFeesAndExpenses],
      queryFn: () =>
        getFundInvestorCashFlows({ fundId, investorType: 'Total', from, to, includeFeesAndExpenses }),
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      // The section renders its own error state with a retry.
      retry: false,
    };
  })
);

export interface IInvestorCashFlowsData {
  /** Every row of the `Total` response, with the fund name joined on for the grid. */
  rows: InvestorCashFlowRow[];
  fundName: string;
  /** The bounds the rows were actually fetched for, echoed by the API; `from` is null for "all dates". */
  fetchedRange: IInvestorCashFlowsDateRange;
  /** True when the response has SLP rows, which is what offers the SLP toggle. */
  hasSlp: boolean;
  /** True only on a first load with nothing to show yet; a refetch keeps the previous rows. */
  isLoading: boolean;
  /** True while any fetch is in flight, including a refetch that is still showing the previous rows. */
  isFetching: boolean;
  hasError: boolean;
  retry: () => void;
}

export function useInvestorCashFlows(fundId: number): IInvestorCashFlowsData {
  const { data, isPending, isFetching, isError, refetch } = useAtomValue(investorCashFlowsQueryJ(fundId));

  return useMemo(() => {
    const fundName = data?.fundName ?? '';
    const rows = (data?.rows ?? []).map((row) => ({ ...row, fundName }));

    return {
      rows,
      fundName,
      fetchedRange: { from: data?.from ?? null, to: data?.to ?? null },
      hasSlp: rows.some((row) => row.investorType === 'SLP'),
      isLoading: isPending,
      isFetching,
      hasError: isError,
      retry: refetch,
    };
  }, [data, isPending, isFetching, isError, refetch]);
}

export function useResetInvestorCashFlowsFilters() {
  const resetType = useResetAtom(investorCashFlowsTypeJ);
  const resetDateRange = useResetAtom(investorCashFlowsDateRangeJ);
  const resetIncludeFees = useResetAtom(investorCashFlowsIncludeFeesJ);

  return useCallback(() => {
    resetType();
    resetDateRange();
    resetIncludeFees();
  }, [resetType, resetDateRange, resetIncludeFees]);
}
